import { Box, Card, CardContent, Typography, Chip } from "@mui/material";

interface TimelineEvent {
  id: string;
  date: string;
  phase: string;
  title: string;
  description: string;
  milestone: boolean;
}

interface TimelineEventProps {
  event: TimelineEvent;
  isLast: boolean;
}

const phaseColors: Record<string, string> = {
  Foundation: "#1976D2",
  "Multi-Tenancy": "#7B1FA2",
  "Enterprise-Grade Features": "#00897B",
  "Production Excellence": "#C62828",
};

export function TimelineEvent({ event, isLast }: TimelineEventProps) {
  const phaseColor = phaseColors[event.phase] || "#1976D2";

  return (
    <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 } }}>
      {/* Date column - hidden on mobile, shown on larger screens */}
      <Box
        sx={{
          width: { xs: 0, sm: 100 },
          flexShrink: 0,
          textAlign: "right",
          display: { xs: "none", sm: "block" },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "text.primary", paddingTop: 2 }}
        >
          {event.date}
        </Typography>
      </Box>

      {/* Timeline dot and line */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 2,
            flexBasis: "15px",
            bgcolor: 'grey.500',
            mb: 0.5,
            opacity: 0.5
          }}
        />
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: event.milestone ? "primary.main" : "secondary.main",
            border: 2,
            borderColor: "background.paper",
            boxShadow: event.milestone ? 2 : 0,
            zIndex: 1,
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flexGrow: 1,
              bgcolor: 'grey.500',
              mt: 0.5,
              opacity: 0.5
            }}
          />
        )}
      </Box>

      {/* Content card */}
      <Card
        variant="outlined"
        sx={{
          flexGrow: 1,
          borderLeft: event.milestone ? 4 : 1,
          borderLeftColor: event.milestone ? "success.main" : "transparent",
          mb: isLast ? 0 : 3,
        }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          {/* Mobile date display */}
          <Box sx={{ display: { xs: "block", sm: "none" }, mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              {event.date}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="overline"
              sx={{ fontSize: "0.7rem", color: phaseColor, fontWeight: 600 }}
            >
              {event.phase}
            </Typography>
            {event.milestone && (
              <Chip
                label="Milestone"
                size="small"
                color="success"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            )}
          </Box>
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{ fontWeight: 600, mb: 0.5, color: "text.primary" }}
          >
            {event.title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {event.description}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
